import React from 'react';
import { ShieldCheckIcon, TruckIcon, ZapIcon, ArrowRightIcon } from 'lucide-react';
import { trackCustom } from '../tracking/metaPixel';
import { useLanguage } from '../../context/LanguageContext';
import { getWhatsAppLink } from '../../utils/whatsappLink';
const VehicleRecommendations = () => {
  const {
    t,
    language
  } = useLanguage();
  const handleCardClick = (category: string) => {
    trackCustom('VehicleInterest', {
      category,
      language,
      action: 'scroll_to_form'
    });
    // Smooth scroll to form
    const form = document.getElementById('lead-form');
    if (form) {
      form.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const icons = [<ShieldCheckIcon className="w-5 h-5 text-primary" data-id="element-402" data-mp="3qwterayds2ix" />, <TruckIcon className="w-5 h-5 text-primary" data-id="element-403" data-mp="3qu777bbhpn0p" />, <ZapIcon className="w-5 h-5 text-primary" data-id="element-404" data-mp="117rfejua3lqt" />];
  // Keeping existing images but ensuring they map correctly to new categories
  // 1. Subaru (using the first image)
  // 2. GMC/Buick (using the second image)
  // 3. Hyundai (using the third image)
  const images = ['https://cdn.magicpatterns.com/uploads/6DToN9caXDD8zvgB41oZGB/Screenshot_2026-02-06_at_5.50.38_AM.png', 'https://cdn.magicpatterns.com/uploads/2AMuADdYoFgDtqikopWwYg/Screenshot_2026-02-06_at_5.44.37_AM.png', 'https://cdn.magicpatterns.com/uploads/hHnFUtuKwCjZdun3oe5cH1/Screenshot_2026-02-06_at_5.45.49_AM.png'];
  return <section className="py-20 px-4 bg-gray-50 border-t border-gray-100" data-id="element-405" data-mp="3jsric4g9ctu6">
      <div className="container mx-auto" data-id="element-406" data-mp="3f0g8a8rszrnw">
        <div className="text-center mb-16" data-id="element-407" data-mp="3ezey06im3978">
          <span className="inline-block py-1 px-3 rounded-full bg-primary/5 text-primary text-xs font-bold tracking-widest uppercase mb-4" data-id="element-408" data-mp="3a1n5pbe21l2i">
            Inventario Premium
          </span>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-primary mb-6" data-id="element-409" data-mp="350z6w3jl3zxq">
            {t.vehicles.headline}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed" data-id="element-410" data-mp="2w1vneygi9iip">
            {t.vehicles.subheadline}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 lg:gap-10" data-id="element-411" data-mp="2lj4nf8fhqi5w">
          {t.vehicles.categories.map((cat, idx) => <div key={idx} className="group bg-white rounded-xl overflow-hidden shadow-[0_2px_16px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300 border border-gray-100 flex flex-col h-full" data-id="element-412" data-mp="2cgbvr42ec4fj" data-mpi={idx}>
              {/* Image Area */}
              <div className="relative aspect-[16/10] overflow-hidden bg-gray-100" data-id="element-413" data-mp="396cqdjpg5f4r">
                <img src={images[idx]} alt={cat.title} className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700 ease-out" data-id="element-414" data-mp="2vf7nm8qs86q7" />
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" data-id="element-415" data-mp="3o1sfdab66kua"></div>

                {/* Brand Badge */}
                <div className="absolute top-4 left-4 bg-primary/90 backdrop-blur-sm text-white text-xs font-bold px-3 py-1.5 rounded shadow-sm tracking-wide uppercase" data-id="element-416" data-mp="62oq6whro0zh">
                  {cat.title}
                </div>
              </div>

              {/* Content Area */}
              <div className="p-8 flex-1 flex flex-col" data-id="element-417" data-mp="au7w2syk5x3v">
                <div className="flex items-start justify-between mb-4" data-id="element-418" data-mp="awsut93k7wxe">
                  <div data-id="element-419" data-mp="3jb1j44z1u1jr">
                    <h3 className="font-heading font-bold text-xl text-primary mb-1" data-id="element-420" data-mp="3jcm5h7b7me6j">
                      {cat.subtitle}
                    </h3>
                    <div className="h-1 w-12 bg-accent rounded-full" data-id="element-421" data-mp="3t8z50ltfrm97"></div>
                  </div>
                  <div className="p-2 bg-gray-50 rounded-lg border border-gray-100 text-primary" data-id="element-422" data-mp="3o9creb9p43zv">
                    {icons[idx]}
                  </div>
                </div>

                <p className="text-gray-600 mb-6 text-sm leading-relaxed" data-id="element-423" data-mp="13jnei3sjys9t">
                  {cat.description}
                </p>

                {/* Models Tags */}
                <div className="flex flex-wrap gap-2 mb-8" data-id="element-424" data-mp="399s7bzzeaef5">
                  {cat.models && cat.models.map((model, i) => <span key={i} className="text-[11px] font-semibold bg-gray-50 border border-gray-200 text-gray-600 px-2.5 py-1 rounded-md uppercase tracking-wide" data-id="element-425" data-mp="3j5lqi27md65w" data-mpi={i}>
                        {model}
                      </span>)}
                </div>

                {/* CTA Area - pinned to bottom */}
                <div className="mt-auto pt-6 border-t border-gray-100" data-id="element-426" data-mp="5qp460phhkft">
                  <button onClick={() => handleCardClick(cat.title)} className="w-full flex items-center justify-center gap-2.5 bg-primary hover:bg-secondary text-white font-bold text-sm tracking-wide h-14 px-5 rounded-[14px] shadow-sm hover:shadow-md transition-all duration-200" data-id="element-427" data-mp="aq20rd65q5vl">
                    <span className="truncate" data-id="element-428" data-mp="249tuy9q7d7f4">
                      {cat.ctaLabel || t.vehicles.cta}
                    </span>
                    <ArrowRightIcon className="w-4 h-4 flex-shrink-0" data-id="element-429" data-mp="1z8d12y64yl81" />
                  </button>

                  <p className="text-center text-[11px] text-gray-400 mt-3 font-medium tracking-wide" data-id="element-430" data-mp="1v8chkz46jju4">
                    {t.vehicles.microcopy}
                  </p>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
};
export default VehicleRecommendations;